import React, { FunctionComponent, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { Box, Card, DialogActions, DialogContent, Toolbar, useTheme } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { strings } from "../../../localization/LocalizedStrings";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { TextField } from "../../TextField";
import { AlertDialog } from "../../AlertDialog";
import { toast } from "react-toastify";
import { FieldTitle } from "../../Text/FieldTitle";
import { ContainedButton } from "../../ContainedButton";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../hooks/isMobile";
import { FreeTextListItem } from "../../FreeTextListItem";
import { ModifyFreeTextAttributeDialog } from "../../attributes/ModifyFreeTextAttributeDialog";
import { IShelf } from "../../../hooks/shelves";
import { useAddShelfSegment } from "../../../hooks/shelfSegments/useAddShelfSegment";
import { useModifyShelfSegment } from "../../../hooks/shelfSegments/useModifyShelfSegment";
import { IShelfSegment } from "../../../hooks/shelfSegments";
import { Firebase } from "../../../services/Firebase";
import { useGetImage, useUploadImage } from "../../../hooks/images";
import { ImageCache } from "../../../models/ImageCache";
import { ImageDropzone } from "../../ImageDropzone";
import { PiperDialog } from "../../PiperDialog";
import { IconType } from "../../../icons";

export const ModifyShelfSegmentDialog: FunctionComponent<IModifyShelfSegmentDialogProps> = ({
  open,
  setOpen,
  shelfSegment,
  shelf,
  callback,
  onDelete
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [dialogTitle, setDialogTitle] = useState(strings.shelf_segment_new_title);
  const { addShelfSegment } = useAddShelfSegment();
  const { modifyShelfSegment } = useModifyShelfSegment();
  const { uploadImage } = useUploadImage();
  const [isModifyDescriptionDialogOpen, setIsModifyDescriptionDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const [imageFile, setImageFile] = useState<File>();
  const [didChangeImage, setDidChangeImage] = useState(false);
  const { getImage } = useGetImage();
  const [image, setImage] = useState();
  const genericImage = new URL("../../../images/generic_shelf_segment.png", import.meta.url).href;
  const fetchImage = async () => {
    if (!shelfSegment?.hasImage) return;
    const response = await getImage({
      name: shelfSegment.uuid,
      width: ImageCache.bigShelfSegmentImageWidth,
      height: ImageCache.bigShelfSegmentImageHeight
    });
    if (StatusCodesHelper.isSuccessful(response.status)) {
      setImage(response.body);
    }
  };
  useEffect(() => {
    setImage(undefined);
    fetchImage().then();
  }, [shelfSegment]);
  const tryToClose = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    if (isLoading) return;
    if (formik.dirty) {
      setIsConfirmCloseAlertOpen(true);
      return;
    }
    handleClose();
  };
  const handleClose = () => {
    setOpen(false);
  };
  const validationSchema = Yup.object({
    name: Yup.string().required(strings.field_missing)
  });
  const formik = useFormik({
    initialValues: {
      name: shelfSegment?.name ?? "",
      description: shelfSegment?.description ?? ""
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async ({ name, description }) => {
      setIsLoading(true);
      let statusCode, responseBody;
      if (shelfSegment) {
        const { status, body } = await modifyShelfSegment({
          uuid: shelfSegment.uuid,
          name: name,
          description: description
        });
        statusCode = status;
        responseBody = body;
      } else {
        const { status, body } = await addShelfSegment({
          name: name,
          description: description,
          shelfId: shelf.uuid
        });
        statusCode = status;
        responseBody = body;
      }
      if (!StatusCodesHelper.isSuccessful(statusCode)) {
        Firebase.shared.logFirebaseEvent("shelfsegment_action");
        setAlertMessage(responseBody.message);
        setAlertIsOpen(true);
      } else {
        if (didChangeImage && imageFile && responseBody.uuid) {
          const newThumbnail = await uploadImage(
            { name: responseBody.uuid, file: imageFile },
            ImageCache.bigShelfSegmentImageWidth,
            ImageCache.bigShelfSegmentImageHeight,
            ImageCache.smallShelfSegmentImageWidth,
            ImageCache.smallShelfSegmentImageHeight
          );
          if (newThumbnail) {
            responseBody.hasImage = true;
            responseBody.thumbnail = newThumbnail;
          }
        }
        if (callback != undefined) {
          callback(responseBody);
        }
        handleClose();
        toast.success(strings.generic_update_success);
      }
      setIsLoading(false);
    },
    validationSchema: validationSchema
  });
  useEffect(() => {
    if (!open) return;
    setDialogTitle(shelfSegment ? strings.shelf_segment_title : strings.shelf_segment_new_title);
    formik.resetForm();
  }, [open]);
  return (
    <FormikProvider value={formik}>
      <PiperDialog fullWidth maxWidth={"xs"} open={open} onClose={tryToClose}>
        <Toolbar>
          <h3>{dialogTitle}</h3>
          <Box sx={{ flexGrow: 1 }} />
          {shelfSegment && (
            <ToolbarButton
              disabled={isLoading}
              onClick={onDelete}
              tooltip={strings.delete}
              iconType={IconType.DeleteForever}
              color={theme.palette.colors?.remove}
            />
          )}
          <ToolbarButton
            loading={isLoading}
            onClick={tryToClose}
            tooltip={strings.close}
            iconType={IconType.Close}
          />
        </Toolbar>
        <DialogContent sx={{ paddingTop: 0, paddingBottom: 0 }}>
          <FieldTitle>{strings.shelf_segment_image_title}</FieldTitle>
          <ImageDropzone
            className={styles.dropzone}
            onImageChanged={file => {
              setImageFile(file);
              setDidChangeImage(true);
            }}
            placeholderImage={genericImage}
            initialImage={
              image
                ? `data:image/png;base64,${image}`
                : shelfSegment?.thumbnail
                  ? `data:image/png;base64,${shelfSegment.thumbnail}`
                  : undefined
            }
            isLoading={false}
          />
          <FieldTitle>{strings.name_title}</FieldTitle>
          <div className={styles.textField}>
            <TextField
              id="name"
              name="name"
              size="small"
              autoFocus={!isMobile}
              placeholder={strings.shelf_segment_name_placeholder}
              value={formik.values.name}
              onChange={formik.handleChange}
            />
          </div>
          <FieldTitle>{strings.collection_description}</FieldTitle>
          <Card className={styles.descriptionCard}>
            <FreeTextListItem
              hideTitle
              detail={formik.values.description ?? "--"}
              maxLines={4}
              onClick={() => setIsModifyDescriptionDialogOpen(true)}
            />
          </Card>
        </DialogContent>
        <DialogActions>
          <ContainedButton type={"submit"} onClick={formik.handleSubmit}>
            {strings.alert_confirm}
          </ContainedButton>
        </DialogActions>
      </PiperDialog>
      <ModifyFreeTextAttributeDialog
        title={strings.collection_description}
        description={strings.edit_description_description}
        placeholder={strings.collection_description}
        value={formik.values.description}
        open={isModifyDescriptionDialogOpen}
        onClose={() => setIsModifyDescriptionDialogOpen(false)}
        onChange={newValue => formik.setFieldValue("description", newValue)}
        onClearValue={() => formik.setFieldValue("description", null)}
      />
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={handleClose}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
    </FormikProvider>
  );
};

export interface IModifyShelfSegmentDialogProps {
  open: boolean;
  setOpen: (state: boolean) => void;
  shelfSegment?: IShelfSegment;
  shelf: IShelf;
  callback?: (newSegment: IShelfSegment) => void;
  onDelete?: () => void;
}
